// libraries
import classNames from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { isValidDateFormat, validateDate } from "../../../setup/util/helpers";
// textAreaStyles
import textAreaStyles from "./textArea.module.scss";
import { InputType } from "../textInput";

interface Props extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  defaultValue?: string | number;
  maxCharacters?: number;
  className?: string;
  // eslint-disable-next-line no-unused-vars
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  type?: InputType;
  description?: string;
  required?: boolean;
  errorFormat?: boolean;
  errorMessage?: string;
  showDescription?: boolean;
  maxInputHeight?: string;
  minInputHeight?: string;
  textRows?: number;
  errorClassName?: string;
  rangeSelector?: [number, number];
  containerClassName?: string;
}

// Extend inline style typings to include the experimental CSS property `fieldSizing`
type TextAreaStyle = React.CSSProperties & { fieldSizing?: string };

const TextArea: React.FC<Props> = ({
  label = "",
  defaultValue = "",
  maxCharacters = 100,
  handleChange,
  description,
  errorClassName,
  required,
  errorFormat = false,
  showDescription = true,
  errorMessage,
  type = InputType.DEFAULT,
  maxInputHeight,
  minInputHeight,
  textRows = 1,
  rangeSelector,
  className,
  containerClassName,
  ...props
}: Props) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [value, setValue] = useState<string>("");
  const [error, setError] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);

  const rows = Math.ceil(maxCharacters / 100);
  const errorClass = error ? "error" : "";

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
      if (e?.target?.value?.length > maxCharacters) {
        if (
          type === InputType["DATE"] &&
          !isValidDateFormat(e?.target?.value)
        ) {
          setError(true);
          setValue(e.target.value);
        } else {
          setError(true);
          setValue(e.target.value);
        }
      } else {
        setError(false);
        setValue(e.target.value);
        handleChange(e);
      }
    },
    [handleChange, maxCharacters, type]
  );

  useEffect(() => {
    const value = inputRef?.current?.value;

    if (focused && ((required && !value) || !!errorMessage)) {
      setError(true);
    }
  }, [errorMessage, focused, required, value, inputRef?.current?.value]);

  useEffect(() => {
    setError(errorFormat);
  }, [errorFormat]);

  const validateNumber = (
    value: React.KeyboardEvent<HTMLTextAreaElement>["key"]
  ) => {
    if (/[٠-٩]/.test(value)) return true;
    if (/[0-9]/.test(value)) return true;
    if (value === ".") return true;
    return false;
  };

  const incrementRange = useCallback(() => {
    if (rangeSelector && Number(inputRef?.current?.value) < rangeSelector[1]) {
      try {
        if (inputRef.current) {
          inputRef.current.value = `${Number(inputRef.current.value) + 1}`;
          inputRef.current.dispatchEvent(
            new Event("textarea", { bubbles: true })
          );
          const event = new Event("change");
          inputRef?.current?.dispatchEvent(event);
          onChange(event as unknown as React.ChangeEvent<HTMLTextAreaElement>);
        }
      } catch (error) {
        console.error({ error });
      }
    }
  }, [onChange, rangeSelector]);

  const decrementRange = useCallback(() => {
    if (rangeSelector && Number(inputRef?.current?.value) > rangeSelector[0]) {
      try {
        if (inputRef.current) {
          inputRef.current.value = `${Number(inputRef.current.value) - 1}`;
          inputRef.current.dispatchEvent(
            new Event("textarea", { bubbles: true })
          );
          const event = new Event("change");
          inputRef?.current?.dispatchEvent(event);
          onChange(event as unknown as React.ChangeEvent<HTMLTextAreaElement>);
        }
      } catch (error) {
        console.error({ error });
      }
    }
  }, [onChange, rangeSelector]);

  const textAreaInlineStyle: TextAreaStyle = {
    fieldSizing: "content",
    minHeight: minInputHeight
      ? minInputHeight
      : !maxInputHeight
      ? `${rows - 0.5}rem`
      : `${rows - 1}rem`,
    maxHeight: maxInputHeight,
  };

  return (
    <div
      className={classNames(
        textAreaStyles["outerContainer"],
        containerClassName
      )}>
      <div
        className={classNames(
          textAreaStyles["container"],
          textAreaStyles[`${errorClass}`]
        )}>
        <div className={"header mb1"}>
          <p
            className={classNames(
              textAreaStyles["hint"],
              textAreaStyles["$g400"]
            )}>
            {label}
          </p>
        </div>

        <textarea
          ref={inputRef}
          rows={textRows || rows}
          className={classNames(textAreaStyles["text_area"], className)}
          maxLength={maxCharacters}
          aria-label="maximum height"
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          style={textAreaInlineStyle}
          onKeyPress={(event) => {
            if (
              (event?.charCode == 13 || event?.code == "Enter") &&
              rows <= 1
            ) {
              event.preventDefault();
            }
            if (!validateDate(event?.key) && type === InputType.DATE) {
              event.preventDefault();
            }
            if (!validateNumber(event?.key) && type === InputType.NUMBER) {
              event.preventDefault();
            }
          }}
          inputMode={type === InputType.DEFAULT ? "text" : "decimal"}
          onFocus={() => {
            setFocused(true);
          }}
          onPaste={(event) => {
            if (type === InputType.NUMBER) {
              event.preventDefault();
            }
          }}
          placeholder={type === InputType["DATE"] ? "dd-mm-yyyy" : ""}
          {...props}
        />
        {rangeSelector ? (
          <div className={textAreaStyles["range_selector"]}>
            <div onClick={incrementRange}>
              <img src="/assets/images/svg/up.svg" alt="up" />
            </div>
            <div onClick={decrementRange}>
              <img src="/assets/images/svg/down.svg" alt="down" />
            </div>
          </div>
        ) : (
          <div className={textAreaStyles["text_length"]}>
            {showDescription && (
              <>
                {description ? (
                  <p>{description}</p>
                ) : (
                  <p
                    className={
                      maxCharacters < String(value)?.length
                        ? textAreaStyles["error_text"]
                        : textAreaStyles[""]
                    }>
                    {maxCharacters}
                    <span>
                      /
                      {
                        String(
                          inputRef?.current?.value || value || defaultValue
                        )?.length
                      }
                    </span>
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
      <div
        className={classNames(
          errorClassName,
          textAreaStyles["error_container"]
        )}>
        {errorMessage && (
          <p dir={"rlt"} className={textAreaStyles["error_text"]}>
            {errorMessage}
          </p>
        )}
      </div>
    </div>
  );
};

export default React.memo(TextArea);
